/**
 * Diagnostic de programme sur le tableau de bord élève (migration 0318) :
 * - `DiagnosticCard` : toujours présent, faire / reprendre / voir + dernier %,
 *   et le lien du rapport dès qu'il existe (5 réponses, partiel si en pause).
 * - `DiagnosticPromptDialog` : fenêtre qui propose le diagnostic UNE fois
 *   (tant qu'il n'est pas commencé et jamais proposé), marquée côté serveur.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ClipboardCheck, Play, Sparkles, X } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { HelpTip } from '@/components/help-tip';
import {
  canRestartDiagnostic,
  diagnosticCta,
  markDiagnosticPrompted,
  reportDiagnostic,
  restartDateLabel,
  shouldPromptDiagnostic,
  type DiagnosticState,
} from '@/lib/skill-diagnostic';

export function DiagnosticCard({ state }: { state: DiagnosticState | undefined }) {
  if (!state?.available || !state.current) return null;
  const { current, last_completed: last } = state;
  const pct = last?.mastery_pct;
  const withReport = reportDiagnostic(state);
  const canRedo = canRestartDiagnostic(current);
  const finished = current.status === 'completed' || current.status === 'abandoned';
  const cta = finished
    ? canRedo
      ? 'Voir / refaire'
      : 'Voir mon diagnostic'
    : diagnosticCta(current);
  return (
    <section
      aria-labelledby="diagnostic-title"
      className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 space-y-2 rounded-2xl border p-4"
    >
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="bg-primary-600 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white">
            <ClipboardCheck className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2
              id="diagnostic-title"
              className="font-display text-ink-900 flex items-center text-base font-bold"
            >
              Mon diagnostic
              <HelpTip topic="diagnostic" label="Diagnostic" />
            </h2>
            <p className="text-ink-600 text-sm">
              {current.status === 'in_progress'
                ? `En cours : ${current.answered_count} question${current.answered_count > 1 ? 's' : ''} sur ${current.question_count}.`
                : typeof pct === 'number'
                  ? `Maîtrise du programme : ${pct} %.${canRedo ? ' Tu peux le refaire.' : ` Prochain diagnostic possible le ${restartDateLabel(current.restart_available_at)}.`}`
                  : 'Une quinzaine de questions pour repérer les SKILLCARDS à retravailler.'}
            </p>
          </div>
        </div>
        <Link
          to="/student/diagnostic"
          aria-label={cta}
          title={cta}
          className="bg-primary-600 hover:bg-primary-700 flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-md active:scale-[0.95]"
        >
          {finished ? (
            <ChevronRight className="h-6 w-6" aria-hidden />
          ) : (
            <Play className="ml-0.5 h-5 w-5 fill-current" aria-hidden />
          )}
        </Link>
      </div>
      {withReport && (
        <Link
          to="/student/diagnostic/rapport/$diagnosticId"
          params={{ diagnosticId: withReport.id }}
          className="text-primary-700 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {withReport.status === 'completed' ? 'Voir mon rapport' : 'Voir mon rapport partiel'}
        </Link>
      )}
    </section>
  );
}

export function DiagnosticPromptDialog({ state }: { state: DiagnosticState | undefined }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const marked = useRef(false);
  const show = shouldPromptDiagnostic(state);

  useEffect(() => {
    if (!show || marked.current || !state?.current) return;
    marked.current = true;
    setOpen(true);
    // Proposée une fois : on le note tout de suite (même si l'élève ferme).
    void markDiagnosticPrompted(state.current.id);
  }, [show, state]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="diagnostic-prompt-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center"
    >
      <div className="ocean:bg-[#061433] relative w-full max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-xl">
        <button
          type="button"
          aria-label="Fermer"
          onClick={() => setOpen(false)}
          className="text-ink-400 absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <span className="bg-primary-600 flex h-12 w-12 items-center justify-center rounded-xl text-white">
          <ClipboardCheck className="h-6 w-6" aria-hidden />
        </span>
        <h2
          id="diagnostic-prompt-title"
          className="font-display text-ink-900 text-xl font-extrabold"
        >
          Fais ton diagnostic
          <HelpTip topic="diagnostic" label="Diagnostic" />
        </h2>
        <p className="text-ink-600 text-sm">
          Une quinzaine de questions sur ce que tu as déjà vu, pour savoir où tu en es et quelles
          SKILLCARDS retravailler. Tu gagnes de l’XP, tu peux t’arrêter et reprendre quand tu veux.
        </p>
        <div className="flex flex-col gap-2">
          <Button
            fullWidth
            onClick={() => {
              setOpen(false);
              void navigate({ to: '/student/diagnostic' });
            }}
          >
            Commencer maintenant
          </Button>
          <Button variant="ghost" fullWidth onClick={() => setOpen(false)}>
            Plus tard (il reste sur mon tableau de bord)
          </Button>
        </div>
      </div>
    </div>
  );
}
